import { useMemo, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { t, tCount, tOr } from '../i18n'
import type { ModuleNode } from '../api/types'
import { NotificationsDrawer } from '../engagement/Notifications'
import { HelpDrawer } from '../help/HelpDrawer'
import { mayTriage } from '../feedback/access'
import { ReportButton } from '../feedback/ReportButton'
import { ErrorNotice, Notice, Pending } from '../ui'
import { documentsEntry, menuEntries, navEntries, type ToolLink } from './nav'
import { ToolsMenu } from './ToolsMenu'
import { directorySectionPath, directorySections } from './DirectoryArea'
import { useActiveProject } from './active-project'
import { ScreenProvider, type ScreenValue } from './screen'
import { UnavailableProject } from './UnavailableProject'
import { levelFor, placementFor, placements, useSession } from './session'
import { useDocumentTitle } from './title'
import { UnsettledWorkProvider, useUnsettledWork, workElsewhere } from './unsettled-work'

/**
 * The screen itself, and the one line that keeps its own state honest about
 * which project it is on.
 *
 * Changing only the `:projectId` segment while the path stays the same is the
 * same route: the router keeps the element mounted and every `useState` in it
 * survives, now holding values from the other job. That is not reachable
 * through the switcher, which navigates to `/p/{id}` and leaves the screen —
 * it is reachable through the address bar, which is a saved link or a typed
 * one, and that is a path people use.
 *
 * Four screens held project-shaped state outside any query key, and the
 * devices screen is why this is a repair rather than a tidy-up: it kept the
 * chosen person's id while the picker, drawing the new project's members,
 * showed "Choose…". Six digits typed after the change set a PIN — a
 * credential — for somebody the operator could no longer see was selected,
 * on a route with no project in it to refuse.
 *
 * Keyed here rather than in each screen, because the next screen to hold a
 * little state is not going to remember this either.
 */
/** The directory module, whose entry is the Directory and permissions area's. */
const DIRECTORY = 'CM-TEN'

export function ProjectScreen() {
  const { projectId = '' } = useParams<{ projectId: string }>()
  // So that work answering late can tell whether this is still the project it
  // was started on.
  useActiveProject(projectId)
  /**
   * The holding for unsettled work sits inside that key and outside the
   * outlet, which is the whole point of putting it here.
   *
   * Outside, because an action that has been dispatched and not answered must
   * survive the screen that started it: the record screen unmounts its panels
   * while an uncached record loads, and the register link unmounts the screen
   * itself. Inside the key, because an action is about a record on this job
   * and changing job should take it away rather than leave it offering a
   * retry from somewhere it does not belong.
   */
  return (
    <UnsettledWorkProvider key={projectId}>
      <HeldElsewhere />
      <Outlet />
    </UnsettledWorkProvider>
  )
}

/**
 * The line that keeps a held action reachable once its screen is gone.
 *
 * Without it, "kept rather than discarded" and "discarded" look the same to
 * the person: the attempt is safe in the store and there is nothing on screen
 * that leads back to it. It names the record and links to it; the retry itself
 * stays on that record's own screen, beside the fields it was typed into and
 * the record it is about.
 */
function HeldElsewhere() {
  const { pathname } = useLocation()
  const work = useUnsettledWork()
  /**
   * By the screen an entry belongs to, not by the record segment of the route.
   *
   * Not every held action is raised against a record. A submittal being
   * created is raised against the *project*, from a screen that has no record
   * segment at all -- so comparing record ids listed that screen's own held
   * creation as work held somewhere else, one line above the card offering it.
   * An entry carries the way back to it, and the way back to the screen you
   * are on is the screen you are on.
   */
  const elsewhere = workElsewhere(work, pathname).filter((entry) => entry.status === 'unsettled')
  if (elsewhere.length === 0) return null
  return (
    <Notice tone="warn">
      <div>{tCount('app.unsettled_elsewhere', elsewhere.length)}</div>
      <ul className="plain">
        {elsewhere.map((entry) =>
          entry.withheld ? (
            // Held, and not named: no record, no way back and no replay while
            // the reader's read of that record is refused.
            <li key={entry.id}>{t('app.unsettled_elsewhere_withheld')}</li>
          ) : (
            <li key={entry.id}>
              <Link to={entry.where}>
                {t('app.unsettled_elsewhere_item', {
                  action: tOr(`app.unsettled_action.${entry.prefix}`, entry.prefix),
                  number: entry.label,
                })}
              </Link>{' '}
              {/* The replay itself, where replaying it from here means the same
                  thing it would mean there. The attempt is frozen with its
                  payload, its key and the record it was raised against, so this
                  is the same request whichever screen it is pressed from -- and
                  an action held for a record the reader has left is otherwise
                  only recoverable by remembering to go back to it. */}
              {entry.retryHere === false ? null : (
                <button type="button" onClick={() => work.again(entry.id)}>
                  {t('app.retry')}
                </button>
              )}
            </li>
          ),
        )}
      </ul>
    </Notice>
  )
}

/**
 * The frame every screen sits in: an app bar with the project switcher and the
 * `?`, a module list down the left, and the report button in the corner.
 * The frame every screen sits in: an app bar with the way back to My Projects,
 * the project switcher and the `?`, a module list down the left, and the report
 * button in the corner.
 *
 * The switcher stays beside that link rather than being replaced by it: moving
 * between two jobs all morning is one control's work, and the chooser is where
 * somebody goes to see what they have rather than to make the next hop.
 *
 * The help route is the one thing a screen has to tell the shell about itself.
 * It is the registered API route with its prefix — `/v1/deficiencies/`, not
 * `/p/<id>/deficiency` — because that is the string the documentation build
 * checks its coverage against, and a browser route the build has never heard
 * of would make coverage a lie.
 */

/**
 * Every named screen inside the shell, with the address it sits at.
 *
 * The menu's own entries, the documents search above them, and the two
 * platform screens listed below them. It exists so the tab and the menu cannot
 * disagree: a module added next month gets a tab name with no change here.
 */
function screenNames(modules: ModuleNode[], projectId: string): { path: string; label: string }[] {
  const documents = documentsEntry(modules, projectId)
  return [
    ...(documents ? [documents] : []),
    ...navEntries(modules, projectId),
    { path: `/p/${projectId}/devices`, label: t('device.admin.title') },
    { path: `/p/${projectId}/system`, label: t('system.title') },
    // The area, and each of its sections by its own name: the tab tells two
    // tabs apart, and two of these sections are both about people.
    ...directorySections().map((section) => ({
      path: directorySectionPath(projectId, section),
      label: section.slug ? section.label : t('area.title'),
    })),
  ]
}

export function Shell() {
  const { projectId } = useParams<{ projectId: string }>()
  const { me, signOut, wording } = useSession()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [helpRoute, setHelpRoute] = useState('/healthz')
  const [moduleCode, setModuleCode] = useState('')
  const [helpOpen, setHelpOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  const placement = projectId ? placementFor(me.data, projectId) : undefined
  const all = placements(me.data)

  const screen = useMemo<ScreenValue | null>(
    () => (placement ? { placement, setHelpRoute, setModuleCode } : null),
    [placement],
  )

  // What the tab says, worked out from the same entries the menu is drawn
  // from rather than from a second list that would drift from it. The match is
  // on the longest path that the address starts with, so a record and the form
  // that raises one both sit under the module they belong to: the tab names
  // the module and the job, which is what tells two tabs apart, and not the
  // record, which the screen itself says. Read here rather than from the
  // module code a screen declares, because two screens in this shell -- the
  // tablets and the people -- are the platform's own and declare the
  // directory's code, so the code would have named the menu entry beside them.
  const menu = me.data && projectId ? screenNames(me.data.modules, projectId) : []
  const here = menu
    .filter((entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
  // The overview's own name is used for the overview's own address and for
  // nothing else. An address inside this job that matches no entry -- a module
  // code this job does not have, a route added before its menu entry -- is
  // named by the job alone, because a tab reading "Overview" over a screen
  // that is not the overview is a worse answer than a tab that only says
  // which job you are in.
  const overview = projectId ? `/p/${projectId}` : ''
  // A job that cannot be placed has no name and no number to put on the tab,
  // and the overview's own name would be a lie there: the tab would read
  // "Overview" over a page saying the project is not available. It gets that
  // page's own title, which names no project because there is none to name.
  const named = placement
    ? (here?.label ?? (pathname === overview ? t('nav.home') : null))
    : t('app.unavailable_project')
  useDocumentTitle(named, placement?.project.number)

  if (me.isPending) return <Pending />
  // Only where there is nothing to keep. This is the frame every screen renders
  // inside, and React Query keeps the last successful answer beside a failed
  // refetch, so deciding on the error alone meant one failed refresh of
  // `/session/me` replaced the whole application -- the app bar, the module
  // list, and whatever somebody was part-way through typing. The session is
  // still good and so is the answer the browser already has; the screens
  // beneath report their own failures. A first read that fails has no session
  // to draw a shell around and still gets the retry.
  if (me.error && !me.data)
    return <ErrorNotice error={me.error} retry={() => void me.refetch()} />
  if (!me.data) return <Navigate to="/signin" replace />
  if (all.length === 0) {
    return (
      <div className="signin">
        <Notice tone="warn">{t('signin.no_projects')}</Notice>
        <button type="button" onClick={signOut}>
          {t('signin.sign_out')}
        </button>
      </div>
    )
  }
  if (!placement || !projectId || !screen) {
    // A URL naming a job this person cannot open says so, on the address they
    // asked for. This used to redirect: first to the person's own first job,
    // which opened the wrong site, then to the chooser, which stopped doing
    // that and still discarded the address without a word. The screen says
    // nothing about the id and nothing that distinguishes a job of another
    // customer from a job that does not exist, which is the same refusal the
    // API makes and the reason it takes no props.
    return <UnavailableProject />
  }

  // The directory module's own entry is drawn below as the area's, beside the
  // platform screens it now holds, rather than once among the modules and
  // again there.
  const entries = menuEntries(me.data.modules, projectId).filter((entry) => entry.code !== DIRECTORY)
  const documents = documentsEntry(me.data.modules, projectId)
  // This reads the directory's level, and the entries below it -- devices,
  // and now access -- are all the directory's, so it is read once rather than
  // once per entry. The name is the devices entry's and is now narrower than
  // what it gates. Renaming it here is what this branch deliberately does not
  // do: a sibling branch adds a third entry that reads this same binding, git
  // merges a rename and a new use of the old name without a word, and the type
  // check is the first thing that notices, after landing. The rename belongs
  // to a change that can see every use of it at once.
  const devicesLocked = levelFor(placement, 'directory') === 'none'
  // Who runs the site gets the page that lists them. Shown only to them: it
  // is not a screen anybody else has a use for, and not a level to lock.
  const platformRole = me.data?.person.platform_role ?? null

  // The same destinations the left-hand list offers, for the tools menu on the
  // bar. Built from the same entries so the two cannot disagree.
  const toolLinks: ToolLink[] = [
    { code: 'home', label: t('nav.home'), path: `/p/${projectId}`, locked: false },
    ...(documents
      ? [
          {
            code: 'documents',
            label: documents.label,
            path: documents.path,
            locked: levelFor(placement, documents.toolCode) === 'none',
          },
        ]
      : []),
    ...entries.map((entry) => ({
      code: entry.code,
      label: entry.label,
      path: entry.path,
      locked: levelFor(placement, entry.toolCode) === 'none',
      preview: !entry.built,
    })),
    { code: DIRECTORY, label: t('area.title'), path: `/p/${projectId}/directory`, locked: devicesLocked },
    // No devices entry: the tablet screens are held back as a future feature,
    // reachable at their addresses and fully working, but linked from nowhere
    // until they are released. Put it back here and below to release them.
    ...(platformRole
      ? [{ code: 'administration', label: t('admin.rail.administration'), path: '/admin', locked: false }]
      : []),
  ]

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('app.skip_to_content')}
      </a>

      <div className="appbar">
        <span className="brand-mark" aria-hidden="true">
          SF
        </span>
        <h1>{t('app.name')}</h1>

        <NavLink to="/" end>
          {t('projects.title')}
        </NavLink>

        {/* Which job you are looking at, said plainly and in one place.
            Before this, the only project text on a module screen was inside
            the switcher below, whose option reads owner, then address, then
            number and name — so on a job sharing an address with others the
            first readable words at the top of the page were the *address*, and
            a reader could take a shared site for the name of their project.
            The project home said both; every module screen said neither.

            The number leads because it is the unambiguous half: two jobs at one
            address can be told apart by it and cannot always be told apart by
            name. The address stays, after it and marked as the parent it is,
            because it is real context and removing it would answer one
            ambiguity by creating another. */}
        <p className="project-identity">
          <strong>
            {placement.project.number} {placement.project.name}
          </strong>
          <span className="muted">
            {' '}
            {t('nav.at_address', { address: placement.address.label })}
          </span>
        </p>

        <label className="visually-hidden" htmlFor="project-switcher">
          {t('nav.switch_project')}
        </label>
        <select
          id="project-switcher"
          value={projectId}
          onChange={(event) => navigate(`/p/${event.target.value}`)}
        >
          {/* The project's own number and name first, and the owner and address
              after them as the parent context they are.

              This is the same fault the identity line above was repaired for,
              left standing in the control beside it. A select is as wide as its
              field, not as wide as its text, so what a reader sees closed is
              the front of the chosen option — and the front was the owner and
              the address, which two jobs at one site share character for
              character. On the demonstration address both options read
              "Northgate Health · Northgate Medical — Building B · …" and the
              half that tells them apart was past the truncation. The number
              leads because it is the unambiguous half: two jobs at one address
              can always be told apart by it and cannot always be told apart by
              name. */}
          {all.map((entry) => (
            <option key={entry.project.id} value={entry.project.id}>
              {entry.project.number} {entry.project.name} · {entry.owner.name} ·{' '}
              {entry.address.label}
            </option>
          ))}
        </select>

        <ToolsMenu links={toolLinks} current={named ?? t('nav.home')} />

        <span className="spacer" />

        <span className="node">
          {t('app.node', { origin: me.data.node.origin })} ·{' '}
          {me.data.node.on_site ? t('app.node_on_site') : t('app.node_cloud')} ·{' '}
          {tCount('app.node_copies', me.data.node.required_copies)}
        </span>

        {/* System status: the VM, its database and the computers on site,
            drawn with what the application can see of them. Open to anybody
            signed in; its provisioning tab is the server's to offer. */}
        <NavLink to={`/p/${projectId}/system`} className="system-button">
          {t('system.button')}
        </NavLink>

        <button type="button" onClick={() => setNotificationsOpen(true)} aria-haspopup="dialog">
          {t('engage.notifications.open')}
        </button>

        <button type="button" onClick={() => setHelpOpen(true)} aria-haspopup="dialog">
          ? <span className="visually-hidden">{t('help.open')}</span>
        </button>
        <span
          className="avatar"
          title={t('app.account', { name: me.data.person.display_name ?? '' })}
          aria-hidden="true"
        >
          {initials(me.data.person.display_name)}
        </span>
        <button type="button" onClick={signOut}>
          {t('signin.sign_out')}
        </button>
      </div>

      <nav className="sidenav" aria-label={t('nav.title')}>
        <h2>{t('nav.title')}</h2>
        <NavLink to={`/p/${projectId}`} end>
          {t('nav.home')}
        </NavLink>
        {documents ? (
          <NavLink
            to={documents.path}
            data-locked={levelFor(placement, documents.toolCode) === 'none'}
          >
            <span>{documents.label}</span>
          </NavLink>
        ) : null}
        {entries.map((entry) => {
          const locked = levelFor(placement, entry.toolCode) === 'none'
          return (
            <NavLink key={entry.code} to={entry.path} data-locked={locked}>
              <span>{entry.label}</span>
              {/* Said before it is opened, so nobody goes looking for a
                  workflow the module does not have a screen for yet. */}
              {entry.built ? null : <span className="tag-preview">{t('nav.preview')}</span>}
              {locked ? <span className="lock">{t('nav.no_access')}</span> : null}
            </NavLink>
          )
        })}
        {/* Directory and permissions: the project directory, People, Access
            and the request queue, which were four entries here, as one entry
            and four sections inside it. Locked on the directory's level, which
            is the level every one of the four was locked on, so it opens for
            exactly the people any of them opened for. Listed rather than
            hidden from people without the level, the way every entry above
            is: the instance decides, and a menu that hides what somebody
            cannot reach teaches them the screen does not exist. */}
        <NavLink to={`/p/${projectId}/directory`} data-locked={devicesLocked}>
          <span>{t('area.title')}</span>
          {devicesLocked ? <span className="lock">{t('nav.no_access')}</span> : null}
        </NavLink>
        {/* The enrolled tablets' screen is not listed here while the tablet
            screens are held back as a future feature. Its route, its server
            and its permission are unchanged; only the link is withheld, and
            withholding a link is not a guard -- the API still decides. */}
        {platformRole ? (
          <NavLink to="/admin" end>
            <span>{t('admin.rail.administration')}</span>
          </NavLink>
        ) : null}
        {/* The build's version is for whoever is asked about a fault, so it sits
            at the foot of the menu rather than in the bar the work is done
            under. A problem report already carries it on its own. */}
        <p className="muted nav-version">{t('app.version', { version: me.data.app_version })}</p>
      </nav>

      <main id="main" tabIndex={-1}>
        {/* Said once, where every screen is, because the fault is not any one
            screen's: the instance's own catalogue feeds court actions, event
            summaries, refusal sentences and every module-declared state name,
            so a session without it shows keys everywhere and used to say
            nothing anywhere. Inside `main` rather than in the bar above it so
            it is read with the screen it applies to, and worded for the person
            in front of it rather than for whoever can fix it. */}
        {wording.failed ? (
          <Notice tone="warn">
            {t('app.wording_unavailable')}{' '}
            <button type="button" onClick={wording.retry}>
              {t('app.retry')}
            </button>
          </Notice>
        ) : null}
        <ScreenProvider value={screen}>
          <ProjectScreen />
        </ScreenProvider>
      </main>

      <ReportButton
        projectId={projectId}
        route={helpRoute}
        appVersion={me.data.app_version}
        personName={me.data.person.display_name}
        moduleCode={moduleCode}
        triage={mayTriage(placement)}
      />

      {helpOpen ? <HelpDrawer route={helpRoute} onClose={() => setHelpOpen(false)} /> : null}
      {notificationsOpen ? (
        // Keyed by the project, like the outlet beside it: what the drawer
        // holds for one job is not carried into the next.
        <NotificationsDrawer
          key={projectId}
          projectId={projectId}
          onClose={() => setNotificationsOpen(false)}
        />
      ) : null}
    </div>
  )
}

/** Up to two letters for the badge on the bar: first and last word. */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}
